import { UIManager } from 'bitmovin-player-ui';
import { customSpecificErrorMessageOverlayConfig } from '../../js/playeractions/handlers/customerrors';
import { handleStreamOptions } from '../streamoptionsHandlers/steamOptionsHandler';
import { createUIContainer } from '../../ui/uicontainer';
import { playerEventMap } from '../../types/events';
import { VOLUME_MAX, VOLUME_MIN, VOLUME_STEP } from './contants';
const externalCallbacksMap = new WeakMap();
export class NpoPlayerAPI {
    constructor(playerAPI) {
        this.playerAPI = playerAPI;
    }
    async load(sourceConfig) {
        await this.playerAPI.load(sourceConfig);
    }
    async play() {
        await this.playerAPI.play();
    }
    pause() {
        this.playerAPI.pause();
    }
    mute() {
        this.playerAPI.mute();
    }
    unmute() {
        this.playerAPI.unmute();
    }
    getVolume() {
        return this.playerAPI.getVolume();
    }
    setVolume(level) {
        const volume = Math.max(VOLUME_MIN, Math.min(VOLUME_MAX, level));
        this.playerAPI.setVolume(volume);
    }
    increaseVolume() {
        const currentVolume = this.getVolume();
        this.setVolume(currentVolume + VOLUME_STEP);
    }
    decreaseVolume() {
        const currentVolume = this.getVolume();
        this.setVolume(currentVolume - VOLUME_STEP);
    }
    setViewMode(viewMode) {
        this.playerAPI.setViewMode(viewMode);
    }
    getViewMode() {
        return this.playerAPI.getViewMode();
    }
    areSubtitlesEnabled() {
        let subsEnabled = false;
        const currentSubtitleTrack = this.playerAPI.subtitles.list();
        for (const subtitle of currentSubtitleTrack) {
            if (subtitle.enabled) {
                subsEnabled = true;
                break;
            }
        }
        return subsEnabled;
    }
    enableSubtitles() {
        const subtitleTracks = this.playerAPI.subtitles.list();
        for (const subtitle of subtitleTracks) {
            if (!subtitle.enabled) {
                this.playerAPI.subtitles.enable(subtitle.id);
                break;
            }
        }
    }
    isPaused() {
        return this.playerAPI.isPaused();
    }
    isMuted() {
        return this.playerAPI.isMuted();
    }
    isLive() {
        return this.playerAPI.isLive();
    }
    async getSupportedDRM() {
        return (await this.playerAPI.getSupportedDRM());
    }
    getSupportedTech() {
        return this.playerAPI.getSupportedTech();
    }
    off(eventType, callback) {
        const externalEventType = playerEventMap[eventType];
        const externalCallback = externalCallbacksMap.get(callback);
        if (externalCallback) {
            this.playerAPI.off(externalEventType, externalCallback);
            externalCallbacksMap.delete(callback);
        }
    }
    on(eventType, callback) {
        const externalEventType = playerEventMap[eventType];
        let externalCallback = externalCallbacksMap.get(callback);
        if (!externalCallback) {
            externalCallback = (args) => {
                callback(args);
            };
            externalCallbacksMap.set(callback, externalCallback);
        }
        this.playerAPI.on(externalEventType, externalCallback);
    }
    isCastAvailable() {
        return this.playerAPI.isCastAvailable();
    }
    seek(time) {
        this.playerAPI.seek(time);
    }
    timeShift(time) {
        this.playerAPI.timeShift(time);
    }
    getContainer() {
        return this.playerAPI.getContainer();
    }
    getNpoPlayerElement() {
        return this.getContainer().querySelector('.bmpui-npo-player');
    }
    addClassToNpoPlayerElement(className) {
        this.getNpoPlayerElement()?.classList.add(className);
    }
    removeClassFromNpoPlayerElement(className) {
        this.getNpoPlayerElement()?.classList.remove(className);
    }
    toggleClassOnNpoPlayerElement(className, toggle) {
        this.getNpoPlayerElement()?.classList.toggle(className, toggle);
    }
    getCurrentTime(mode) {
        return this.playerAPI.getCurrentTime(mode);
    }
    getDuration() {
        return this.playerAPI.getDuration();
    }
    getTimeShift() {
        return this.playerAPI.getTimeShift();
    }
    async scheduleAds(adConfig) {
        return this.playerAPI.ads.schedule(adConfig);
    }
    getActiveAdBreak() {
        return this.playerAPI.ads.getActiveAdBreak();
    }
    discardAdBreak(adBreakId) {
        return this.playerAPI.ads.discardAdBreak(adBreakId);
    }
    getConfig(mergedConfig) {
        return this.playerAPI.getConfig(mergedConfig);
    }
    async createUIManager(playerContext, variant) {
        if (playerContext.npoplayer.uiManager === undefined || variant !== playerContext.npoplayer.variant) {
            const uiConfig = {
                errorMessages: customSpecificErrorMessageOverlayConfig,
                disableAutoHideWhenHovered: true,
                seekbarSnappingEnabled: false
            };
            playerContext.npoplayer.uiManager = new UIManager(this.playerAPI, createUIContainer(playerContext, this.playerAPI, variant), uiConfig);
            playerContext.npoplayer.variant = variant;
        }
        handleStreamOptions(playerContext);
    }
}
